// src/components/users/UserFormModal.js
// --- UPDATE (User Management) ------------------------------------------------------------
// Create / edit an account. Sections: identity (name, username, email, phone, employee no.),
// job (department, job title → FlowSense role advisor suggests the role), role (only roles the
// signed-in user may give, each explained) and access (status; on create: password — typed with a
// strength meter or generated, "must change at next sign-in").
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, FormControl, FormLabel, FormHelperText, FormErrorMessage, Input, InputGroup, InputRightElement, InputLeftElement,
    Select, SimpleGrid, VStack, HStack, Box, Text, Badge, Icon, IconButton, Switch, Progress, Textarea, Tooltip, Alert, AlertIcon,
    useColorModeValue, Divider,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdRefresh, MdOutlineRemoveRedEye, MdPerson, MdEmail, MdPhone, MdBadge, MdContentCopy, MdLock } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { FiUserPlus } from 'react-icons/fi';
import { ROLE_OPTIONS, canAssignRole, roleMeta } from '../../config/roles';
import { suggestRole, explainRole, passwordStrength, generatePassword } from './userAiIntel';

const EMPTY = { first_name: '', last_name: '', username: '', email: '', phone: '', employee_id: '', department: '', job_title: '', role: 'tech', status: 'active', notes: '', password: '', must_change_password: true };
const DEPARTMENTS = ['Operations', 'Customer Care', 'Field Services', 'Water Distribution', 'Sewerage', 'Road Works', 'Engineering', 'Meter Services', 'Stores & Inventory', 'Finance', 'IT'];

const slug = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '');

const UserFormModal = ({ isOpen, onClose, user = null, actorRole, onSubmit, passwordMinLength = 10, defaultRole = null }) => {
    const isEdit = !!user;
    const [form, setForm] = useState(EMPTY);
    const [touched, setTouched] = useState({});
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [usernameEdited, setUsernameEdited] = useState(false);
    const muted = useColorModeValue('gray.500', 'gray.400');
    const aiBg = useColorModeValue('purple.50', 'whiteAlpha.100');
    const aiBorder = useColorModeValue('purple.100', 'whiteAlpha.200');

    useEffect(() => {
        if (!isOpen) return;
        setError(null); setTouched({}); setShowPw(false); setUsernameEdited(isEdit);
        setForm(isEdit ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, user[k] ?? EMPTY[k]])), password: '' } : { ...EMPTY, role: defaultRole && canAssignRole(actorRole, defaultRole) ? defaultRole : EMPTY.role });
    }, [isOpen, isEdit, user, defaultRole, actorRole]);

    const set = (k) => (e) => {
        const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
        setForm((f) => {
            const next = { ...f, [k]: v };
            if (!isEdit && !usernameEdited && (k === 'first_name' || k === 'last_name')) {
                const u = `${slug(next.first_name)}${next.last_name ? `.${slug(next.last_name)}` : ''}`;
                next.username = u.slice(0, 50);
            }
            return next;
        });
    };

    const roleOptions = useMemo(() => ROLE_OPTIONS.filter((r) => canAssignRole(actorRole, r.id) || (isEdit && r.id === user?.role)), [actorRole, isEdit, user]);
    const advice = useMemo(() => suggestRole(form.job_title, form.department), [form.job_title, form.department]);
    const roleInfo = useMemo(() => explainRole(form.role), [form.role]);
    const strength = passwordStrength(form.password, passwordMinLength);

    const errors = {
        username: !/^[A-Za-z0-9._-]{3,50}$/.test(form.username) ? '3–50 letters, numbers, dot, dash or underscore' : null,
        email: !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email) ? 'Enter a valid email' : null,
        password: !isEdit && form.password && !strength.ok ? `Needs ${strength.hints.filter((h) => !h.includes('symbol')).join(', ')}` : null,
        role: !roleOptions.some((r) => r.id === form.role) ? 'You cannot give this role' : null,
    };
    const invalid = Object.values(errors).some(Boolean);

    const submit = async (e) => {
        e.preventDefault();
        setTouched({ username: true, email: true, password: true, role: true });
        if (invalid) return;
        setBusy(true); setError(null);
        try {
            const body = { ...form };
            if (isEdit) { delete body.password; delete body.must_change_password; delete body.username; }
            else if (!body.password) delete body.password;
            await onSubmit(body);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const selfEdit = isEdit && user?.is_self;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside" isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent as="form" onSubmit={submit} borderRadius="2xl" noValidate>
                <ModalHeader>
                    <HStack spacing={3}>
                        <Box w="40px" h="40px" bgGradient="linear(to-br, purple.500, blue.500)" borderRadius="lg" display="flex" alignItems="center" justifyContent="center">
                            <Icon as={FiUserPlus} color="white" boxSize={5} />
                        </Box>
                        <Box>
                            <Text>{isEdit ? `Edit ${user.display_name || user.username}` : 'Add user'}</Text>
                            <Text fontSize="xs" color={muted} fontWeight="normal">{isEdit ? `@${user.username}` : 'Create a FlowSense account — FlowSense suggests the role from the job title.'}</Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={5}>
                        {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}

                        <Box>
                            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Identity</Text>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <FormControl><FormLabel fontSize="sm">First name</FormLabel><Input value={form.first_name} onChange={set('first_name')} placeholder="Hanna" autoComplete="off" /></FormControl>
                                <FormControl><FormLabel fontSize="sm">Last name</FormLabel><Input value={form.last_name} onChange={set('last_name')} placeholder="Rolle" autoComplete="off" /></FormControl>
                                <FormControl isRequired isInvalid={touched.username && !!errors.username} isReadOnly={isEdit}>
                                    <FormLabel fontSize="sm">Username</FormLabel>
                                    <InputGroup>
                                        <InputLeftElement pointerEvents="none"><Icon as={MdPerson} color="gray.400" /></InputLeftElement>
                                        <Input value={form.username} onChange={(e) => { setUsernameEdited(true); set('username')(e); }} onBlur={() => setTouched((t) => ({ ...t, username: true }))} placeholder="hanna.rolle" autoComplete="off" />
                                    </InputGroup>
                                    {isEdit ? <FormHelperText fontSize="xs">Usernames cannot change (they appear in work orders and audits).</FormHelperText> : <FormErrorMessage>{errors.username}</FormErrorMessage>}
                                </FormControl>
                                <FormControl isRequired isInvalid={touched.email && !!errors.email}>
                                    <FormLabel fontSize="sm">Email</FormLabel>
                                    <InputGroup>
                                        <InputLeftElement pointerEvents="none"><Icon as={MdEmail} color="gray.400" /></InputLeftElement>
                                        <Input type="email" value={form.email} onChange={set('email')} onBlur={() => setTouched((t) => ({ ...t, email: true }))} placeholder="name@wsc.com.bs" autoComplete="off" />
                                    </InputGroup>
                                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Phone</FormLabel>
                                    <InputGroup>
                                        <InputLeftElement pointerEvents="none"><Icon as={MdPhone} color="gray.400" /></InputLeftElement>
                                        <Input value={form.phone} onChange={set('phone')} placeholder="242-555-0100" />
                                    </InputGroup>
                                    {roleMeta(form.role).field && !form.phone && <FormHelperText fontSize="xs" color="orange.400">Field roles need a phone number for dispatch and escalations.</FormHelperText>}
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Employee no.</FormLabel>
                                    <InputGroup>
                                        <InputLeftElement pointerEvents="none"><Icon as={MdBadge} color="gray.400" /></InputLeftElement>
                                        <Input value={form.employee_id} onChange={set('employee_id')} placeholder="WSC-1042" />
                                    </InputGroup>
                                </FormControl>
                            </SimpleGrid>
                        </Box>

                        <Box>
                            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Job & role</Text>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <FormControl>
                                    <FormLabel fontSize="sm">Department</FormLabel>
                                    <Input list="fs-departments" value={form.department} onChange={set('department')} placeholder="Customer Care" />
                                    <datalist id="fs-departments">{DEPARTMENTS.map((d) => <option key={d} value={d} />)}</datalist>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Job title</FormLabel>
                                    <Input value={form.job_title} onChange={set('job_title')} placeholder="Helpdesk Agent, Technician, Field Supervisor…" />
                                </FormControl>
                            </SimpleGrid>
                            {advice && advice.role !== form.role && roleOptions.some((r) => r.id === advice.role) && !selfEdit && (
                                <HStack mt={3} p={3} bg={aiBg} borderWidth="1px" borderColor={aiBorder} borderRadius="lg" spacing={3}>
                                    <Icon as={MdAutoAwesome} color="purple.400" />
                                    <Text fontSize="sm" flex={1}>FlowSense suggests <b>{advice.label}</b> — {advice.why}</Text>
                                    <Button size="xs" colorScheme="purple" onClick={() => setForm((f) => ({ ...f, role: advice.role }))}>Use {advice.label}</Button>
                                </HStack>
                            )}
                            <FormControl mt={3} isRequired isInvalid={touched.role && !!errors.role} isDisabled={selfEdit}>
                                <FormLabel fontSize="sm">Role</FormLabel>
                                <Select value={form.role} onChange={set('role')} aria-label="Role">
                                    {roleOptions.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                                </Select>
                                {selfEdit ? <FormHelperText fontSize="xs">You cannot change your own role.</FormHelperText> : <FormErrorMessage>{errors.role}</FormErrorMessage>}
                            </FormControl>
                            <Box mt={2} p={3} borderRadius="lg" borderWidth="1px" borderColor={aiBorder}>
                                <HStack mb={1} spacing={2}>
                                    <Badge colorScheme={roleMeta(form.role).color}>{roleInfo.label}</Badge>
                                    {roleInfo.field && <Badge variant="outline">Field app</Badge>}
                                    {roleInfo.external && <Badge colorScheme="yellow" variant="outline">External</Badge>}
                                </HStack>
                                <Text fontSize="xs" color={muted}>{roleInfo.summary}</Text>
                            </Box>
                        </Box>

                        <Box>
                            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Access</Text>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <FormControl isDisabled={selfEdit}>
                                    <FormLabel fontSize="sm">Status</FormLabel>
                                    <Select value={form.status} onChange={set('status')} aria-label="Status">
                                        <option value="active">Active — can sign in</option>
                                        <option value="on_leave">On leave — not dispatched</option>
                                        <option value="inactive">Inactive — account disabled</option>
                                    </Select>
                                </FormControl>
                                {!isEdit && (
                                    <FormControl isInvalid={touched.password && !!errors.password}>
                                        <FormLabel fontSize="sm">Password</FormLabel>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none"><Icon as={MdLock} color="gray.400" /></InputLeftElement>
                                            <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={set('password')} placeholder="Leave empty to generate one" autoComplete="new-password" pr="6rem" />
                                            <InputRightElement w="6rem">
                                                <HStack spacing={0}>
                                                    <Tooltip label="Generate a strong password"><IconButton size="xs" variant="ghost" icon={<Icon as={MdRefresh} />} aria-label="Generate password" onClick={() => { setForm((f) => ({ ...f, password: generatePassword() })); setShowPw(true); }} /></Tooltip>
                                                    <IconButton size="xs" variant="ghost" icon={<Icon as={showPw ? RiEyeCloseLine : MdOutlineRemoveRedEye} />} aria-label={showPw ? 'Hide password' : 'Show password'} onClick={() => setShowPw((v) => !v)} />
                                                    {form.password && <IconButton size="xs" variant="ghost" icon={<Icon as={MdContentCopy} />} aria-label="Copy password" onClick={() => navigator.clipboard?.writeText(form.password)} />}
                                                </HStack>
                                            </InputRightElement>
                                        </InputGroup>
                                        {form.password ? (
                                            <>
                                                <Progress mt={2} size="xs" borderRadius="full" value={(strength.score / 4) * 100} colorScheme={!strength.ok ? 'red' : strength.score >= 4 ? 'green' : strength.score >= 3 ? 'teal' : 'yellow'} aria-label="Password strength" />
                                                <Text fontSize="xs" color={muted} mt={1}>{strength.label}{strength.hints.length ? ` · add ${strength.hints.join(', ')}` : ''}</Text>
                                            </>
                                        ) : <FormHelperText fontSize="xs">FlowSense will generate a 14-character temporary password and show it once.</FormHelperText>}
                                        <FormErrorMessage>{errors.password}</FormErrorMessage>
                                    </FormControl>
                                )}
                            </SimpleGrid>
                            {!isEdit && (
                                <HStack mt={3}>
                                    <Switch id="mcp" isChecked={form.must_change_password} onChange={set('must_change_password')} colorScheme="purple" />
                                    <FormLabel htmlFor="mcp" mb={0} fontSize="sm">Must change the password at the next sign-in</FormLabel>
                                </HStack>
                            )}
                            <FormControl mt={3}>
                                <FormLabel fontSize="sm">Notes</FormLabel>
                                <Textarea value={form.notes} onChange={set('notes')} rows={2} placeholder="Shift, zone, certifications…" />
                            </FormControl>
                        </Box>
                        <Divider />
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button type="submit" colorScheme="purple" isLoading={busy} leftIcon={<Icon as={FiUserPlus} />}>{isEdit ? 'Save changes' : 'Create user'}</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

UserFormModal.propTypes = {
    isOpen: PropTypes.bool, onClose: PropTypes.func.isRequired, user: PropTypes.object, actorRole: PropTypes.string,
    onSubmit: PropTypes.func.isRequired, passwordMinLength: PropTypes.number, defaultRole: PropTypes.string,
};

export default UserFormModal;
